import { useState, useEffect } from "react";
import api from "../lib/api";
import PodLayout from "../components/PodLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { ChatCircle, Plus, ArrowLeft, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  "General",
  "Leadership & School Growth",
  "Career & Professional Development",
  "Operations & Administration",
  "Welfare & Safeguarding",
  "Finance & Business",
  "Technology & Innovation",
];

export default function PodForumPage() {
  const { user } = useAuth();
  const isStaff = ["super_admin", "admin"].includes(user?.role);
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "General" });
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(null); // {thread, replies}
  const [reply, setReply] = useState("");

  const load = () => {
    const q = filter ? `?category=${encodeURIComponent(filter)}` : "";
    api.get(`/pod/forum/threads${q}`).then(r => setThreads(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/pod/forum/threads", form);
      toast.success("Thread created");
      setOpen(false);
      setForm({ title: "", body: "", category: "General" });
      load();
    } catch { toast.error("Failed to create thread"); }
    setSaving(false);
  };

  const openThread = async (id) => {
    try {
      const r = await api.get(`/pod/forum/threads/${id}`);
      setActive(r.data);
    } catch { toast.error("Could not load thread"); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/pod/forum/threads/${active.thread.id}/replies`, { body: reply });
      setReply("");
      const r = await api.get(`/pod/forum/threads/${active.thread.id}`);
      setActive(r.data);
      load();
    } catch { toast.error("Reply failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this thread?")) return;
    try { await api.delete(`/pod/forum/threads/${id}`); toast.success("Deleted"); setActive(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (active) {
    const { thread, replies } = active;
    return (
      <PodLayout>
        <div className="p-6 md:p-8 max-w-4xl">
          <button onClick={() => setActive(null)} className="text-sm text-slate-600 hover:text-slate-950 flex items-center gap-1 mb-4" data-testid="pod-forum-back">
            <ArrowLeft size={16} /> Back to forum
          </button>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">{thread.category}</p>
                  <h1 className="text-2xl font-bold text-slate-950 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{thread.title}</h1>
                  <p className="text-xs text-slate-500 mt-1">by {thread.author_name} · {new Date(thread.created_at).toLocaleString()}</p>
                </div>
                {(isStaff || thread.author_id === user?.id) && (
                  <button onClick={() => remove(thread.id)} className="text-red-600 hover:text-red-800" data-testid="pod-forum-delete-thread"><Trash size={18} /></button>
                )}
              </div>
              {thread.body && <p className="text-sm text-slate-700 mt-4 whitespace-pre-wrap">{thread.body}</p>}
            </CardContent>
          </Card>

          <div className="mt-6 space-y-3">
            {replies.length === 0 && <p className="text-sm text-slate-500">No replies yet — be the first to respond.</p>}
            {replies.map(r => (
              <Card key={r.id}>
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-slate-700">{r.author_name}</p>
                  <p className="text-[10px] text-slate-500">{new Date(r.created_at).toLocaleString()}</p>
                  <p className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{r.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-6">
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Reply</Label>
            <Textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Share your thoughts…" data-testid="pod-forum-reply-input" />
            <Button onClick={sendReply} className="mt-2 bg-slate-950 hover:bg-slate-800 text-white" data-testid="pod-forum-reply-btn">Post Reply</Button>
          </div>
        </div>
      </PodLayout>
    );
  }

  return (
    <PodLayout>
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">POD Community</p>
            <h1 className="text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>POD Forum</h1>
            <p className="text-sm text-slate-600 mt-1">Discuss leadership topics with the wider community.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter || "__all"} onValueChange={(v) => setFilter(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="pod-forum-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="pod-forum-new-btn">
              <Plus size={16} className="mr-1" /> New Thread
            </Button>
          </div>
        </div>

        {threads.length === 0 && (
          <Card><CardContent className="p-8 text-center">
            <ChatCircle size={32} className="mx-auto text-slate-400" />
            <p className="text-sm text-slate-600 mt-2">No threads yet. Start the first conversation.</p>
          </CardContent></Card>
        )}

        <div className="space-y-2">
          {threads.map(t => (
            <Card key={t.id} className="cursor-pointer hover:border-amber-400 transition-colors" onClick={() => openThread(t.id)} data-testid={`pod-thread-${t.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">{t.category}</p>
                  <p className="text-sm font-bold text-slate-950 truncate">{t.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">by {t.author_name} · {new Date(t.last_activity_at).toLocaleString()}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-slate-500">Replies</p>
                  <p className="text-lg font-bold text-slate-950">{t.replies_count || 0}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Forum Thread</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pod-forum-new-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What's on your mind?" data-testid="pod-forum-new-title" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Body (optional)</Label>
              <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="pod-forum-new-body" />
            </div>
            <Button onClick={create} disabled={saving} className="w-full bg-slate-950 hover:bg-slate-800 text-white" data-testid="pod-forum-new-submit">
              {saving ? "Posting…" : "Post Thread"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PodLayout>
  );
}
